import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Instagram, ArrowLeft, Search,
  Loader2, AlertCircle, Sparkles, RefreshCw,
  ChevronRight, X, Palette, Users, FileText, Check, Layers
} from 'lucide-react';
import { contentService } from '../../../services/ContentService';
import CreditsExhaustedModal from '../../../components/contents/CreditsExhaustedModal';

/**
 * Loading de Análise com Steps
 */
const AnalysisLoadingScreen = ({ username, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [dots, setDots] = useState('');

  const steps = [
    { id: 1, message: 'Conectando ao Instagram', icon: Instagram },
    { id: 2, message: 'Buscando informações do perfil', icon: Users },
    { id: 3, message: 'Extraindo cores predominantes', icon: Palette },
    { id: 4, message: 'Gerando prompt automático', icon: FileText },
  ];

  // Animação dos pontinhos
  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots(prev => prev === '...' ? '' : prev + '.');
    }, 400);
    return () => clearInterval(dotsInterval);
  }, []);

  // Avançar steps
  useEffect(() => {
    if (currentStep < steps.length) {
      const timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, steps.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-6 text-white text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <Instagram className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold mb-1">Analisando @{username}</h2>
          <p className="text-white/80 text-sm">Isso pode levar alguns segundos{dots}</p>
        </div>

        <div className="p-6">
          <div className="space-y-3">
            {steps.map((step) => {
              const StepIcon = step.icon;
              const isCompleted = step.id < currentStep;
              const isCurrent = step.id === currentStep;
              const isPending = step.id > currentStep;

              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-4 p-3 rounded-xl transition-all duration-500 ${
                    isCurrent ? 'bg-amber-50 border-2 border-amber-200' : ''
                  } ${isPending ? 'opacity-40' : 'opacity-100'}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                    isCompleted ? 'bg-green-500 text-white' 
                    : isCurrent ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white' 
                    : 'bg-gray-100 text-gray-400'
                  }`}>
                    {isCompleted ? <Check className="w-5 h-5" /> 
                    : isCurrent ? <Loader2 className="w-5 h-5 animate-spin" /> 
                    : <StepIcon className="w-5 h-5" />}
                  </div>
                  <span className={`font-medium ${
                    isCurrent ? 'text-gray-900' : isCompleted ? 'text-gray-600' : 'text-gray-400'
                  }`}>
                    {step.message}{isCurrent && <span className="text-amber-500">{dots}</span>}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="mt-6 mb-4">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500"
                style={{ width: `${(currentStep / steps.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="text-center pt-2">
            <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4 inline mr-1" />Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Preview do Perfil (SEM IMAGENS)
 */
const ProfilePreview = ({ profileData, onEdit, prompt, onPromptChange }) => {
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);

  return (
    <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
            <Instagram className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold">@{profileData.username}</h3>
            <p className="text-sm text-white/80">Perfil analisado com sucesso</p>
          </div>
          <button onClick={onEdit} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Bio */}
        {profileData.bio && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Bio</h4>
            <p className="text-gray-700 text-sm">{profileData.bio}</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-4 bg-amber-50 rounded-xl">
            <p className="text-xl font-bold text-gray-900">{profileData.followers?.toLocaleString() || 0}</p>
            <p className="text-xs text-gray-500">Seguidores</p>
          </div>
          <div className="text-center p-4 bg-amber-50 rounded-xl">
            <p className="text-xl font-bold text-gray-900">{profileData.following?.toLocaleString() || 0}</p>
            <p className="text-xs text-gray-500">Seguindo</p>
          </div>
          <div className="text-center p-4 bg-amber-50 rounded-xl">
            <p className="text-xl font-bold text-gray-900">{profileData.posts_count || 0}</p>
            <p className="text-xs text-gray-500">Posts</p>
          </div>
        </div>

        {/* Cores Extraídas */}
        {profileData.colors && profileData.colors.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Cores Extraídas
            </h4>
            <div className="flex flex-wrap gap-2">
              {profileData.colors.map((color, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl text-sm font-semibold text-amber-800 capitalize"
                >
                  {color}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Prompt Editável */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Prompt {isEditingPrompt ? '(Editando)' : 'Sugerido'}
            </h4>
            <button
              onClick={() => setIsEditingPrompt(!isEditingPrompt)}
              className={`text-xs font-medium px-3 py-1 rounded-lg transition-colors ${
                isEditingPrompt 
                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {isEditingPrompt ? 'Salvar' : 'Editar'}
            </button>
          </div>
          
          {isEditingPrompt ? (
            <textarea
              value={prompt}
              onChange={(e) => onPromptChange(e.target.value)}
              className="w-full p-4 border-2 border-amber-300 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 resize-none"
              rows={4}
              placeholder="Descreva o que você quer criar..."
            />
          ) : (
            <div 
              className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200 cursor-pointer hover:border-amber-300 transition-colors"
              onClick={() => setIsEditingPrompt(true)}
            >
              <p className="text-sm text-amber-900">{prompt}</p>
              <p className="text-xs text-amber-600 mt-2">Clique para editar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Componente Principal - Instagram Wizard
 */
const InstagramWizard = () => {
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [error, setError] = useState(null);
  const [creditsExhausted, setCreditsExhausted] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [editablePrompt, setEditablePrompt] = useState('');

  // Formato: 'post' ou 'carousel'
  const [contentFormat, setContentFormat] = useState('post');
  const [carouselSize, setCarouselSize] = useState(5);
  // ✅ Fase 4: dimensoes — "Feed Novo" (portrait 1080x1350) é o PADRÃO
  const [imageDimensions, setImageDimensions] = useState('portrait');

  const validateUsername = (value) => {
    if (!value) return 'Digite um nome de usuário válido';
    if (value.length > 30) return 'Username muito longo';
    if (!/^[a-z0-9._]+$/.test(value)) return 'Username contém caracteres inválidos';
    return null;
  };

  const handleAnalyzeProfile = async () => {
    const cleanUsername = username.trim().toLowerCase();
    const validationError = validateUsername(cleanUsername);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setStep(2);

    try {
      const result = await contentService.analyzeInstagram(cleanUsername);
      if (result.data) {
        setProfileData(result.data);
        setEditablePrompt(result.data.generated_prompt || '');
        setStep(3);
      } else {
        throw new Error('Não foi possível analisar o perfil');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erro ao analisar perfil');
      setStep(1);
    }
  };

  const handleGenerateContents = async () => {
    if (!profileData) return;
    
    // Validar prompt
    if (!editablePrompt || editablePrompt.trim().length < 10) {
      setError('O prompt deve ter pelo menos 10 caracteres');
      return;
    }

    setIsGenerating(true);
    setStep(4);

    try {
      let prompt = editablePrompt.trim();

      // Adicionar contexto de carrossel ao prompt
      if (contentFormat === 'carousel') {
        prompt += `\n\n🎠 FORMATO: CARROSSEL INSTAGRAM (${carouselSize} slides)`;
        prompt += `\nEste conteudo sera usado para um carrossel no Instagram com ${carouselSize} slides. Mantenha coerencia visual entre os slides.`;
      }

      const batchData = contentFormat === 'carousel' ? {
        user_prompt: prompt,
        generation_settings: { creatomate_count: carouselSize, dimensions: imageDimensions },
        enabled_models: { creatomate: true },
        content_types: ['marketing'],
        batch_type: 'carousel',
        carousel_size: carouselSize,
        prefers_gpt_image: imageDimensions !== 'square',
      } : {
        user_prompt: prompt,
        generation_settings: {
          dall_e_3_count: 0,
          flux_11_count: 0,
          flux_2_pro_count: 0,
          flux_kontext_pro_count: 0,
          nano_banana_count: 0,
          sora_count: 0,
          veo_31_count: 0,
          placid_count: null,
          dimensions: imageDimensions
        },
        enabled_models: {
          dall_e_3: false,
          flux_11: false,
          flux_2_pro: false,
          flux_kontext_pro: false,
          nano_banana: false,
          sora: false,
          veo_31: false,
          placid: true
        },
        content_types: ['marketing'],
        prefers_gpt_image: imageDimensions !== 'square',
      };

      const response = await contentService.createBatch(batchData);
      if (response.data?.batch_id) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        navigate(`/contents/batch/${response.data.batch_id}`);
      }
    } catch (err) {
      // ⚠️ detail do 403 é um OBJETO ({error, message, remaining...}); jogar objeto
      // no state quebrava o React ("Objects are not valid as a React child") e
      // derrubava a página inteira. Sempre extrair string.
      const detail = err.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (detail && typeof detail === 'object') {
        msg = detail.remaining === 0
          ? 'Seus créditos acabaram. Escolha um plano para continuar criando conteúdos.'
          : (detail.message || 'Você não tem créditos suficientes para esta criação.');
      } else {
        msg = err.message || 'Erro ao criar conteúdos';
      }
      setError(msg);
      setCreditsExhausted(err.response?.status === 403);
      if (detail && typeof detail === 'object' && typeof detail.remaining === 'number') {
        setCreditsRemaining(detail.remaining);
      }
      setStep(3);
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setUsername('');
    setProfileData(null);
    setError(null);
    setStep(1);
    setIsGenerating(false);
    setEditablePrompt('');
    setContentFormat('post');
    setCarouselSize(5);
  };

  // Step 2: Loading
  if (step === 2) {
    return <AnalysisLoadingScreen username={username} onCancel={() => setStep(1)} />;
  }

  // Step 4: Gerando
  if (step === 4) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center relative">
            <Sparkles className="w-12 h-12 text-white" />
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 animate-ping opacity-30" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Criando seus conteúdos!</h2>
          <p className="text-gray-600 mb-6">Gerando imagens baseadas no perfil <strong>@{profileData?.username}</strong></p>
          <div className="flex items-center justify-center gap-2 text-sm text-amber-600 bg-amber-50 py-3 px-4 rounded-xl">
            <Loader2 className="w-4 h-4 animate-spin" />Redirecionando...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/contents/new" className="inline-flex items-center gap-2 text-gray-600 hover:text-amber-600 mb-6">
            <ArrowLeft className="w-5 h-5" /><span className="font-medium">Voltar</span>
          </Link>

          {/* Progress */}
          <div className="flex items-center gap-2 mb-6">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${step >= 1 ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg' : 'bg-gray-100 text-gray-500'}`}>
              <Instagram className="w-4 h-4" />Perfil
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
            <div className={`px-4 py-2 rounded-xl text-sm font-medium ${step >= 3 ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg' : 'bg-gray-100 text-gray-500'}`}>
              Confirmar
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
            <div className={`px-4 py-2 rounded-xl text-sm font-medium ${step >= 4 ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg' : 'bg-gray-100 text-gray-500'}`}>
              Gerar
            </div>
          </div>
        </div>

        {/* Step 1: Input */}
        {step === 1 && (
          <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-8 md:p-12">
            <div className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Instagram className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">Digite seu @Instagram</h1>
            <p className="text-lg text-gray-600 text-center mb-10 max-w-md mx-auto">
              Vamos analisar as cores do seu perfil e criar conteúdos personalizados
            </p>

            <div className="space-y-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-5 pointer-events-none">
                  <span className="text-2xl text-gray-400 font-medium">@</span>
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value.replace('@', '').trim().toLowerCase());
                    setError(null);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && username && handleAnalyzeProfile()}
                  placeholder="seu_perfil"
                  className={`w-full pl-12 pr-32 py-5 text-2xl font-medium border-2 rounded-2xl focus:outline-none focus:ring-4 ${
                    error ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-gray-200 focus:border-amber-500 focus:ring-amber-100'
                  }`}
                  autoFocus
                />
                <button
                  onClick={handleAnalyzeProfile}
                  disabled={!username}
                  className={`absolute inset-y-2 right-2 px-6 rounded-xl font-semibold flex items-center gap-2 ${
                    !username ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:from-amber-500 hover:to-orange-600 shadow-lg'
                  }`}
                >
                  <Search className="w-5 h-5" />Analisar
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-3 text-red-600 text-sm bg-red-50 border border-red-200 p-4 rounded-xl">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1">{error}</span>
                  <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <p className="text-sm text-gray-500 text-center">O perfil deve ser público</p>
            </div>

            <div className="mt-10 pt-8 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center mb-4">O que vamos extrair:</p>
              <div className="flex flex-wrap justify-center gap-3">
                {['Bio do perfil', 'Cores da marca', 'Estilo visual'].map((item) => (
                  <span key={item} className="px-4 py-2 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 rounded-xl text-sm font-medium border border-amber-200">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && profileData && (
          <div className="space-y-6">
            <ProfilePreview 
              profileData={profileData} 
              onEdit={handleReset}
              prompt={editablePrompt}
              onPromptChange={setEditablePrompt}
            />

            {/* Seleção de Formato */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Formato do conteúdo</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  onClick={() => setContentFormat('post')}
                  className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                    contentFormat === 'post'
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-200'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    contentFormat === 'post' ? 'bg-gradient-to-br from-green-500 to-emerald-500' : 'bg-gray-100'
                  }`}>
                    <FileText className={`w-5 h-5 ${contentFormat === 'post' ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-semibold text-gray-900 block">Post</span>
                    <span className="text-xs text-gray-500">Posts para o feed</span>
                  </div>
                </button>

                <button
                  onClick={() => setContentFormat('carousel')}
                  className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                    contentFormat === 'carousel'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-200'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    contentFormat === 'carousel' ? 'bg-gradient-to-br from-purple-500 to-indigo-500' : 'bg-gray-100'
                  }`}>
                    <Layers className={`w-5 h-5 ${contentFormat === 'carousel' ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-semibold text-gray-900 block">Carrossel</span>
                    <span className="text-xs text-gray-500">2 a 5 slides</span>
                  </div>
                </button>
              </div>

              {/* Seletor de slides para carrossel */}
              {contentFormat === 'carousel' && (
                <div className="mb-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <p className="text-xs font-semibold text-purple-800 mb-2">Quantos slides?</p>
                  <div className="flex gap-2">
                    {[2, 3, 4, 5].map(size => (
                      <button
                        key={size}
                        onClick={() => setCarouselSize(size)}
                        className={`flex-1 py-2 rounded-lg border-2 font-bold text-sm transition-all ${
                          carouselSize === size
                            ? 'border-purple-500 bg-purple-500 text-white'
                            : 'border-purple-200 bg-white text-purple-700 hover:border-purple-300'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ✅ Fase 4: Seletor de tamanho da imagem */}
              <div className="mb-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-xs font-semibold text-emerald-900 mb-2">Tamanho da imagem</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'portrait', label: 'Feed Novo', size: '1080x1350' },
                    { value: 'square', label: 'Feed', size: '1080x1080' },
                    { value: 'story', label: 'Stories', size: '1080x1920' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setImageDimensions(opt.value)}
                      className={`py-2 rounded-lg border-2 transition-all ${
                        imageDimensions === opt.value
                          ? 'border-emerald-500 bg-emerald-500 text-white'
                          : 'border-emerald-200 bg-white text-emerald-700 hover:border-emerald-300'
                      }`}
                    >
                      <div className="font-bold text-xs">{opt.label}</div>
                      <div className={`text-[10px] font-mono ${imageDimensions === opt.value ? 'text-white/80' : 'text-emerald-500'}`}>{opt.size}</div>
                    </button>
                  ))}
                </div>
                {imageDimensions !== 'square' && (
                  <p className="mt-2 text-[10px] text-emerald-700">
                    ✨ Vertical usa gpt-image-2 (OpenAI) — qualidade superior em texto e logo.
                  </p>
                )}
              </div>

              {error && !creditsExhausted && (
                <div className="flex items-center gap-3 text-red-600 text-sm bg-red-50 border border-red-200 p-4 rounded-xl mb-4">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1">{error}</span>
                  <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={handleReset} className="flex-1 py-3.5 px-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 flex items-center justify-center gap-2">
                  <RefreshCw className="w-4 h-4" />Trocar Perfil
                </button>
                <button
                  onClick={handleGenerateContents}
                  disabled={isGenerating}
                  className="flex-1 py-3.5 px-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-500 hover:to-orange-600 flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg"
                >
                  {isGenerating ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />Criando...</>
                  ) : contentFormat === 'carousel' ? (
                    <><Sparkles className="w-5 h-5" />Gerar Carrossel ({carouselSize} slides)</>
                  ) : (
                    <><Sparkles className="w-5 h-5" />Gerar Conteúdos</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Popup de créditos insuficientes (mesmo em todos os fluxos) */}
      <CreditsExhaustedModal
        open={creditsExhausted}
        balance={creditsRemaining}
        message={error}
        onClose={() => { setCreditsExhausted(false); setError(null); }}
      />
    </div>
  );
};

export default InstagramWizard;